import React, { useState, useEffect } from "react";
import { CssBaselineProps } from "@material-ui/core";
import { useLocationSearch } from "Hooks/useLocationSearch";
import { ProviderLocation } from "generated/graphql";
import { ReactSelectStyles } from "Components/Pages/Users/UserFilters/UserFiltersMenu/multiSelectStyles";
import { LocationsMenuList } from "./LocationsMenuList/LocationsMenuList";
import { NullableOption, Option } from "Shared/types/Option";
import { DPReactSelect } from "Components/DP/DPReactSelect/DPReactSelect";

export interface LocationSelectProps {
  filter: NullableOption[];
  onFilterChange: (newFilters: Option[]) => void;
  maxMenuHeight?: number;
  placeholder?: string;
  styles?: ReactSelectStyles;
  disabled?: boolean;
  selectAllOption?: boolean;
  isFullPermissionSelected?: boolean;
}

export const LocationSelect = (props: LocationSelectProps) => {
  const {
    filter,
    onFilterChange,
    maxMenuHeight = 200,
    placeholder,
    styles,
    disabled,
    selectAllOption,
    isFullPermissionSelected,
  } = props;
  const { locations, searchTerm, setSearchTerm, loading, increasePage, totalCount } = useLocationSearch();

  const [currentLocationOptions, setCurrentLocationOptions] = useState([] as ProviderLocation[]);
  const resetCurrentLocationOptions = () => setCurrentLocationOptions([]);
  const [value, setValue] = useState<NullableOption[]>(filter);

  useEffect(() => {
    if (!loading) {
      const newLocationOptions = [...currentLocationOptions].concat(locations);
      setCurrentLocationOptions(newLocationOptions);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, locations]);

  const allOption = {
    label: "Select all",
    value: null,
  };

  const onChangeHandler = (selected: NullableOption[]) => {
    if (selected[selected.length-1]?.value === allOption.value) {
      setValue([{ label: "All Locations selected", value: null }]);
      onFilterChange([]);
    } else {
      const realOptions = selected.filter(option => option.value !== null) as Option[];
      setValue(realOptions);
      onFilterChange(realOptions);
    }
  };

  useEffect(() => {
    if (isFullPermissionSelected) {
      onChangeHandler([allOption]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFullPermissionSelected]);

  return (
    <DPReactSelect
      value={value}
      defaultValue={filter}
      options={[...(selectAllOption ? [allOption] : []), ...currentLocationOptions.map((location) => ({
        value: location.id,
        label: location.name,
      }))]}
      onChange={onChangeHandler}
      isMulti
      closeMenuOnSelect={false}
      isDisabled={disabled || isFullPermissionSelected}
      styles={{
        ...styles,
        multiValueRemove: (base: CssBaselineProps) =>  disabled
          ? { ...base, display: "none" }
          : base,
      }}
      onInputChange={(newInput: string) => {
        if(newInput !== searchTerm) {
          resetCurrentLocationOptions();
          setSearchTerm(newInput);
        }
      }}
      hideSelectedOptions={false}
      maxMenuHeight={maxMenuHeight}
      placeholder={placeholder}
      // Props to be used in LocationsMenuList
      hasNextPage={currentLocationOptions.length < totalCount}
      isNextPageLoading={loading}
      loadNextPage={increasePage}
      itemSize={40}
      components={{
        MenuList: LocationsMenuList,
      }}
    />
  );
};
